import { useState, useEffect, useMemo } from "react";

export interface ChatParams {
  tenantId: string | null;
  chatId: string | null;
  locale: string;
  hasRequiredParams: boolean;
}

/**
 * Reads a parameter from the URL query string (case-insensitive)
 */
const getUrlParameter = (name: string): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  
  // Try exact match first
  let value = urlParams.get(name);
  
  // If not found, try case-insensitive search
  if (!value) {
    const lowerName = name.toLowerCase();
    urlParams.forEach((val, key) => {
      if (!value && key.toLowerCase() === lowerName) {
        value = val;
      }
    });
  }
  
  // Remove any quotes from the value (e.g. "it" -> it)
  if (value) {
    return value.replace(/^["']|["']$/g, "").trim();
  }
  return null;
};

/**
 * Hook for reading chat parameters from URL with reactivity
 */
export const useChatParams = (): ChatParams => {
  const [params, setParams] = useState<ChatParams>(() => {
    const tenantId = getUrlParameter("tenantId");
    const chatId = getUrlParameter("chatId");
    const locale = getUrlParameter("locale") || "it";
    
    console.log("🔗 Initial URL params:", { tenantId, chatId, locale, search: window.location.search });
    
    return {
      tenantId,
      chatId,
      locale,
      hasRequiredParams: !!tenantId,
    };
  });

  // Listen for URL changes (popstate, pushstate)
  useEffect(() => {
    const updateParams = () => {
      const tenantId = getUrlParameter("tenantId");
      const chatId = getUrlParameter("chatId");
      const locale = getUrlParameter("locale") || "it";
      
      console.log("🔗 URL params updated:", { tenantId, chatId, locale, search: window.location.search });
      
      setParams({
        tenantId,
        chatId,
        locale,
        hasRequiredParams: !!tenantId,
      });
    };

    window.addEventListener("popstate", updateParams);
    
    // Also check on mount in case params changed
    updateParams();
    
    return () => {
      window.removeEventListener("popstate", updateParams);
    };
  }, []);

  return params;
};

/**
 * Updates a parameter in the URL without reloading the page
 */
export const updateUrlParameter = (name: string, value: string): void => {
  const url = new URL(window.location.href);
  url.searchParams.set(name, value);
  window.history.pushState({}, "", url.toString());
};

